/**
 * Data Type Registry - extensible via TypeScript declaration merging.
 * Plugins add their types by augmenting this interface.
 *
 * Example (in plugin):
 *   declare module '@nodes/Endpoint' {
 *     interface DataTypeRegistry {
 *       audio: 'audio';
 *     }
 *   }
 */
export interface DataTypeRegistry {
  number: 'number';
  string: 'string';
  boolean: 'boolean';
  array: 'array';
  any: 'any';
}

/**
 * Union of all registered data types.
 * Automatically includes plugin-registered types via declaration merging.
 */
export type DataType = DataTypeRegistry[keyof DataTypeRegistry];

/**
 * Data type specifier — a single DataType or a union of two types
 * separated by '|' (e.g., 'array|polylines').
 * Both halves are type-checked against the DataTypeRegistry.
 */
export type DataTypeSpec = DataType | `${DataType}|${DataType}`;

/**
 * Data flow semantics - how data is delivered through an endpoint.
 * - param: stored value, persisted (default)
 * - event: pushed with time + value, not persisted
 * - trigger: pushed with time only, not persisted
 * - stream: continuous signal flow (e.g., audio)
 */
export type DataFlow = 'param' | 'event' | 'trigger' | 'internal' | 'stream';

// Note: No 'select' type - UI renders a select control when options[] is present

/**
 * Domain - categorizes nodes for menu grouping
 */
export enum Domain {
  Data = 'data',
  Audio = 'audio',
  Video = 'video',
}

/**
 * Base configuration for all endpoint types.
 * Includes optional data-specific fields that the runtime may use.
 *
 * The generic parameters preserve specific types for type-safe filtering
 * in TypedParams/TypedTriggers/TypedEvents.
 */
export interface Endpoint<
  TDataType extends DataTypeSpec = DataTypeSpec,
  TDataFlow extends DataFlow = DataFlow
> {
  id?: string;  // Optional - derived from object key in endpoint definitions
  dataType?: TDataType;  // defaults to 'number' at runtime if not specified
  dataFlow?: TDataFlow;  // defaults to 'param' at runtime if not specified
  label?: string;
  info?: string;  // Tooltip text for the endpoint
  isInput?: boolean;
  isOutput?: boolean;
  allowInput?: boolean;  // If false, hide the isInput checkbox in config UI
  allowOutput?: boolean; // If false, hide the isOutput checkbox in config UI
  primary?: boolean;
  group?: string;
  groupOrder?: number;
  name?: string;
  order?: number;
  disabled?: boolean;
  transient?: boolean;
  value?: any; // default value in config, actual value in endpoint

  // Data-specific fields (optional, used by data endpoints)
  min?: number;
  max?: number;
  step?: number;
  options?: readonly any[];
  optionLabels?: readonly string[];
  optionInfo?: readonly string[];
  scaleIncomingValues?: boolean;
  inheritMetadata?: boolean;
  isPresetParam?: boolean;
  /** Fixed array length for array slider controls */
  length?: number;

  /**
   * Push serialized value to store for UI access.
   * Params are always pushed to store. This is for non-param endpoints
   * (e.g., internal outputs) that need values accessible to visualizations.
   */
  pushToStore?: boolean;

  /** True if this is a custom param added via config (not a base endpoint) */
  custom?: boolean;

  /** Whether expression evaluation is active */
  useExp?: boolean;

  /**
   * Expression source for computing value as function of time.
   * Expression endpoints are evaluated pull-based at render time (not pushed).
   * The expression receives a context with musical time and sibling param metadata.
   */
  exp?: string;

  /** DataTypes this input accepts (e.g. ['video', 'hydra', 'polylines']). Defaults to own dataType. */
  accepts?: string[];

  /** Display value read-only with no editable control. Use for computed outputs like count. */
  readonly?: boolean;

  /** If provided, renders the stringified value for internal endpoints. */
  stringify?: (value: any) => string;

  /** Flagged as tweakable — included in project-level snapshots */
  tweakable?: boolean;
}


/**
 * UI state for an endpoint. Extends Endpoint with UI-specific fields
 * and makes optional fields required for consistent UI rendering.
 */
export interface EndpointState extends Omit<Endpoint, 'disabled' | 'dataType' | 'dataFlow' | 'exp' | 'stringify'> {
  // Required versions of optional config fields
  dataType: DataTypeSpec;  // Required for UI (default: 'number')
  dataFlow: DataFlow;  // Required for UI (default: 'param')
  label: string;
  min: number;
  max: number;
  step: number;
  options: any[];
  optionLabels: string[];
  optionInfo: string[];
  isInput: boolean;
  isOutput: boolean;
  allowInput: boolean;
  allowOutput: boolean;
  inheritMetadata: boolean;
  scaleIncomingValues: boolean;
  // Renamed for UI clarity
  isDisabled: boolean;
  // UI-specific fields
  isExposed: boolean;
  /** Serialized expression source (e.g., "Math.sin(t * 0.01)") */
  exp?: string;
  /** Custom control identifier for rendering (e.g. 'definitionSelect') */
  control?: string;
  /** DataTypes this input accepts. Defaults to own dataType. */
  accepts: string[];
  /** Display value read-only with no editable control. */
  readonly: boolean;
  /** Renders a stringified value for display. Returns '' to show nothing. */
  stringify: (value: any) => string;
  /** Flagged as tweakable — included in project-level snapshots */
  tweakable: boolean;
}



/**
 * Musical time context passed to function-valued endpoints.
 */
export interface MusicalTime {
  bars: number;
  beats: number;
  ticks: number;
}

/**
 * Param metadata available to function endpoints.
 * Includes current value and range info for sibling params on the same node.
 */
export interface ParamInfo {
  value: any;
  min: number;
  max: number;
  step: number;
}

/**
 * Context passed to function-valued endpoint evaluators at render time.
 * Functions receive musical time and metadata for all sibling params on the node.
 */
export interface FunctionContext {
  time: MusicalTime;
  params: Record<string, ParamInfo>;
}

/**
 * Stream data types - endpoints that pass objects/functions rather than serializable values.
 * These don't store values in the usual sense.
 */
const streamDataTypes = new Set(['audio', 'video', 'hydra', 'nodebox', 'three', 'two']);

/**
 * Check if an endpoint dataType stores serializable data values.
 * Stream types (audio, video, etc.) pass objects/functions, not storable values.
 */
export function isDataEndpoint(dataType: string): boolean {
  return !streamDataTypes.has(dataType);
}
